/* eslint-disable @next/next/no-img-element */

import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useRouter } from "next/router";

import axios, { Canceler } from "axios";
import useCancel from "../../Hooks/CancelFetch";
import useDebounce from "../../Hooks/Debounce";
import Select from "react-select";
import Footer from "../../Footer";
import Header from "../../Header";
import PageHeader from "../../PageHeader";
import SweepVehicleFilters from "../../VehicleFilters/Sweep";
import MobileControl from "../../SearchSettings/MobileControl";
import SearchMenu from "../../SearchSettings/SearchMenu";
import Loader from "../../Loader";
import useMobile from "../../Hooks/Mobile";

import Backend, { SweepBackend } from "../../../utils/Backend";
import { usedFilters as defaultFilters } from "../../../helper/filters";
import { usedSortOptions } from "../../../helper/filters";
import { googleAdIds, vehicleTypes } from "../../../helper/constants";
import { renderVehicles } from "../../../utils/renderer";

import IVehicle from "../../../interfaces/IVehicle";
import iFilters from "../../../interfaces/iFilters";

import styles from "../styles.module.scss";
import { vehicleTheme, getVehicleStyle, getVehicleStyleBoi } from "../../../helper/style";
import SearchPageBackground from "../../../public/assets/images/headers/used-evs-header.jpg";
import SweepFiltersManager from "../../SweepFiltersManager";
import GoogleAd from "../../GoogleAd";
import { useIsBoiWebsite } from "../../Context/WebsiteType";

const PAGE_SIZE = 12;
const CACHE_SIZE = 100; // 100 vehicles

export default function SweepVehicleSearch() {
	const [count, setCount] = useState(0);
	const [vehicles, setVehicles] = useState<IVehicle[]>([]);
	const [page, setPage] = useState(1);

	const [loading, setLoading] = useState(true);
	const [loadMore, setLoadMore] = useState(false);
	const [loadedAllVehicles, setLoadedAllVehicles] = useState(false);
	const [message, setMessage] = useState("");

	const [filters, setFilters] = useState<iFilters>({ ...defaultFilters });

	const [refresh, setRefresh] = useState(false);
	const [updateURL, setUpdateURL] = useState(false);

	// mobile && tablet needs
	const [displayFilters, setDisplayFilters] = useState(false);
	const [displaySort, setDisplaySort] = useState(false);
	const [hideMenuOnSort, setHideMenuOnSort] = useState(false);

	const mobile = useMobile(700);
	const tablet = useMobile(1140);
	const smallDesktop = useMobile(1440);

	const cancelSearchRef = useRef<Canceler>();
	const cancelNevoRef = useRef<Canceler>();
	const cancelCountRef = useRef<Canceler>();
	const cancelOverridesRef = useRef<Canceler>();
	const vehiclesRef = useRef<HTMLDivElement>(null);

	const [cacheStatus, setCacheStatus] = useState("");
	const router = useRouter();

	const isBoiWebsite = useIsBoiWebsite();

	// trying to get vehicles from cached results
	useEffect(() => {
		if (!router.isReady) return;

		const cachedURL = sessionStorage.getItem("filtersURL");

		if (cachedURL === router.asPath && cacheStatus === "") {
			const cachedVehicles = sessionStorage.getItem("vehiclesResults");

			if (cachedVehicles) {
				const allVehicles = JSON.parse(cachedVehicles);

				setVehicles(allVehicles);

				if (allVehicles.length === 0) {
					setMessage("We found no vehicles for your request. Please update filters");
					setCount(0);
					setLoadedAllVehicles(true);
				} else {
					const cachedStartAt = sessionStorage.getItem("startAt");
					const cachedCount = sessionStorage.getItem("totalCount");
					const cachedLoadedAll = sessionStorage.getItem("loadedAll");

					if (cachedCount) setCount(parseInt(cachedCount));
					if (cachedStartAt) setPage(parseInt(cachedStartAt));
					if (cachedLoadedAll) setLoadedAllVehicles(cachedLoadedAll === "true");
				}

				setLoading(false);

				setCacheStatus("processed");
			} else {
				setCacheStatus("not found");
			}
		} else if (cacheStatus === "") {
			setCacheStatus("not found");
		}
	}, [router, cacheStatus]);

	// Refresh search query
	useEffect(() => {
		if (refresh) {
			setMessage("");
			setRefresh(false);
			setLoadedAllVehicles(false);
			setPage(1);
			setVehicles([]);
			setLoading(true);
			setLoadMore(true);
		}
	}, [refresh]);

	const lazyLoading = useCallback(() => {
		if (!loadedAllVehicles && vehiclesRef?.current) {
			let offsetTop = 0;
			let element: any = vehiclesRef.current;
			while (element) {
				offsetTop += element.offsetTop - element.scrollTop + element.clientTop;
				element = element.offsetParent;
			}

			const endOfVehicles = vehiclesRef.current.clientHeight + offsetTop;
			const currentScroll = window.scrollY + window.innerHeight;

			if (currentScroll > endOfVehicles + 100) {
				if (!loading && !loadMore) {
					setLoadMore(true);
					setLoading(true);
				}
			}
		}
	}, [loadMore, loadedAllVehicles, loading]);

	// Enable/Disable lazy loading if out of vehicles
	useEffect(() => {
		if (!loadedAllVehicles) {
			window.addEventListener("scroll", lazyLoading);
			return () => window.removeEventListener("scroll", lazyLoading);
		}
	}, [lazyLoading, loadedAllVehicles, vehicles]);

	// displaying no found message, if no vehicles found
	useEffect(() => {
		if (vehicles.length === 0 && loadedAllVehicles) {
			setMessage("We found no vehicles for your request. Please update filters");
			setCount(0);
		}
	}, [vehicles, loadedAllVehicles]);

	// If all vehicles were fetched
	useEffect(() => {
		if (vehicles.length !== 0 && vehicles.length >= count) {
			setLoadedAllVehicles(true);
		}
	}, [count, vehicles]);

	useCancel(cancelSearchRef.current);
	useCancel(cancelCountRef.current);
	useCancel(cancelNevoRef.current);
	useCancel(cancelOverridesRef.current);

	/**
	 * @description Adding Nevo Vehicles to Used Vehicles (in order to get proper images)
	 * @param fetchedVehicles - array of Used Vehicles
	 * @returns returns array of Used Vehicles with Nevo Vehicles
	 */
	const addNevoVehicles = async (fetchedVehicles: any[]) => {
		let nevo_ids: any[] = fetchedVehicles.map(({ model }: any) => model.nevo_model_id);

		// filtering out empty values
		nevo_ids = nevo_ids.filter((id) => !!id);

		// if we have Nevo IDs
		if (nevo_ids.length) {
			// removing empty values
			nevo_ids = nevo_ids.filter((id) => !!id);

			// unique values
			nevo_ids = nevo_ids.filter((element, index) => {
				return nevo_ids.indexOf(element) === index;
			});

			// getting Nevo vehicles
			const { promise, cancel } = Backend.getVehiclesByIDs(nevo_ids);
			cancelNevoRef.current = cancel;

			try {
				const result = await promise;

				const nevoVehicles = result.data.content.vehicles;

				// adding Nevo vehicles to fetched vehicles
				fetchedVehicles.forEach((vehicle: any) => {
					const nevoVehicle = nevoVehicles.find((nevoVehicle: any) => nevoVehicle.id === vehicle.model.nevo_model_id);

					if (nevoVehicle) {
						vehicle.nevo = JSON.parse(JSON.stringify(nevoVehicle)); // assigning the deepest possible copy
					}
				});
			} catch (err) {
				if (!axios.isCancel(err)) {
					console.error("Failed to retrieve Nevo vehicles");
				}
			}
		}

		return fetchedVehicles;
	};
	const addImagesOverrides = async (fetchedVehicles: any[]) => {
		if (!fetchedVehicles.length) return;

		const vids = fetchedVehicles.map((vehicle) => vehicle.id);

		const { promise, cancel } = Backend.getUsedEVImagesOverrides(vids);
		cancelOverridesRef.current = cancel;

		try {
			const results = await promise;
			const imagesOverrides = results.data.content;

			// if there is no overrides
			if (!imagesOverrides?.length) {
				return;
			}

			// overriding images for fetched vehicles
			imagesOverrides.forEach((override: any) => {
				const vehicle = fetchedVehicles.find((vehicle: any) => override.vehicle_id === vehicle.id);

				if (vehicle) {
					vehicle.nevo.image_overrides = override.image_override;
				}
			});
		} catch (err) {
			if (!axios.isCancel(err)) {
				console.error("Failed to retrieve images overrides");
			}
		}
	};

	const errorCleanup = () => {
		setVehicles([]);
		setCount(0);
		setMessage("Currently, we're experiencing technical issues, but we're working on that. Please, come back to search for new Electric Vehicles soon!");
		setLoading(false);
	};

	// FETCH VEHICLES BASES ON filters
	useEffect(() => {
		if (!loadedAllVehicles && loadMore) {
			setLoadMore(false);

			const fetchData = async () => {
				let totalCount = count;
				// means fetching first time, so need to retrieve feed count
				if (page === 1) {
					const { promise: promCount, cancel: cancelCount } = SweepBackend.getUsedVehiclesCounts(filters);
					cancelSearchRef.current = cancelCount;

					try {
						const result = await promCount;
						totalCount = result.data.count;
					} catch (err) {
						if (!axios.isCancel(err)) {
							console.error("Failed to retrieve vehicles count");
							errorCleanup();
							return;
						}
					}
				}

				const { promise, cancel } = SweepBackend.getUsedVehicles(filters, page, PAGE_SIZE);
				cancelSearchRef.current = cancel;

				promise
					.then(async (response) => {
						let allVehicles: IVehicle[] = [];

						if (Object.keys(response.data?.results)?.length !== 0) {
							let fetchedVehicles: any[] = await addNevoVehicles(response.data.results);

							// remove vehicles without nevo
							fetchedVehicles = fetchedVehicles.filter((vehicle: any) => {
								if (!vehicle.nevo) {
									--totalCount; // update count
								}
								return !!vehicle.nevo;
							});

							// remove duplicated vehicles
							fetchedVehicles = fetchedVehicles.filter((vehicle: any) => {
								return !vehicles.find((processedVehicle: any) => {
									if (processedVehicle.id === vehicle.id) {
										--totalCount; // if found duplicate, update count
									}

									return processedVehicle.id === vehicle.id;
								});
							});

							// remove vehicles older than 89 days (due to vehicle images clean up policy)
							const daysAgo = new Date(new Date().setDate(new Date().getDate() - 89));
							fetchedVehicles = fetchedVehicles.filter((vehicle: any) => {
								// if found outdated vehicle, reduce totalCount
								const vehicleDate = new Date(vehicle.updated_at);
								if (vehicleDate < daysAgo) {
									--totalCount;
									return false;
								}
								return true;
							});

							// fetch vehicles overrides for each vehicle
							await addImagesOverrides(fetchedVehicles);

							if (count !== totalCount) {
								setCount(totalCount);
								sessionStorage.setItem("totalCount", `${totalCount}`);
							}

							allVehicles = vehicles.concat(fetchedVehicles);
							setVehicles(allVehicles);

							// all vehicles fetched
							if (allVehicles.length === 0) {
								setLoadedAllVehicles(true);
								sessionStorage.setItem("loadedAll", "true");
							} else {
								const nextPage = page + 1;
								setPage(nextPage);

								sessionStorage.setItem("loadedAll", "false");
								sessionStorage.setItem("startAt", `${nextPage}`);
							}
						} else {
							// all vehicles fetched
							setLoadedAllVehicles(true);
							sessionStorage.setItem("loadedAll", "true");
						}

						let vehiclesToCache = allVehicles;

						// caching last CACHE_SIZE vehicles
						if (allVehicles.length > CACHE_SIZE) {
							vehiclesToCache = allVehicles.slice(allVehicles.length - CACHE_SIZE);
						}

						sessionStorage.setItem("vehiclesResults", JSON.stringify(vehiclesToCache));

						setLoading(false);
					})
					.catch((err) => {
						if (axios.isCancel(err)) return;

						if (err.response.status === 404) {
							setLoading(false);
							setLoadedAllVehicles(true);
							sessionStorage.setItem("loadedAll", "true");
						} else {
							console.error("Error occurred while fetching vehicles");
							errorCleanup();
						}
					});
			};

			fetchData();
		}
	}, [filters, loadMore, loadedAllVehicles, loading, page, vehicles, count]);

	const handleSelectChange = useCallback(
		(value: any, name: string) => {
			setFilters({ ...filters, [name]: value });
			setRefresh(true); // to apply Filters
			setUpdateURL(true); // to update URL
		},
		[filters]
	);

	const resetFilters = () => {
		// to save selected sort by option
		setFilters({ ...defaultFilters });

		setRefresh(true);
		setUpdateURL(true);
	};

	const { processingURL, options } = SweepFiltersManager(filters, setFilters, false, setRefresh, cacheStatus, updateURL, setUpdateURL, tablet);

	const renderFilters = (onClose?: () => void) => {
		return <SweepVehicleFilters processingURL={processingURL} filters={filters} options={options} handleSelectChange={handleSelectChange} resetFilters={resetFilters} onClose={onClose} />;
	};

	const renderedSortBySelector = useMemo(
		() => (
			<div className={styles.selectHolder}>
				<Select
					instanceId="sort-by"
					value={filters.order_by}
					options={usedSortOptions}
					onChange={(value: any) => {
						handleSelectChange(value, "order_by");

						if (tablet) setHideMenuOnSort(true);
					}}
					styles={(isBoiWebsite ? getVehicleStyleBoi() : getVehicleStyle())}
					theme={vehicleTheme}
				/>
			</div>
		),
		[filters, handleSelectChange, tablet, isBoiWebsite]
	);

	const hideMenu = () => {
		setDisplayFilters(false);
		setDisplaySort(false);
		const htmlDoc = document.children[0] as HTMLElement;
		if (htmlDoc.style) {
			htmlDoc.style.overflow = "";
			document.body.style.overflow = "";
		}
	};

	// hide menu when resize from mobile/tablet to desktop
	if (tablet !== undefined && !tablet && (displayFilters || displaySort)) {
		hideMenu();
	}

	const slugMakes = useMemo(() => {
		if (!router.isReady) return;
		const slug = router.query.slug as string;

		if (slug) {
			if (slug.includes("@")) return slug.split("@")[1];
			else return slug;
		}
	}, [router]);

	const slugModels = useMemo(() => {
		if (!router.isReady) return;
		const slug = router.query.slug as string;

		if (slug && slug.includes("@")) {
			return slug.split("@")[0];
		}
	}, [router]);

	// Causes one more render to get the google ad displayed
	useDebounce(vehicles, 600);

	return (
		<>
			<Header transparent black noShadowBox={tablet} hideBanner/>

			<PageHeader backgroundImage={SearchPageBackground.src} className={`${styles.pageHeader} ${styles.usedPageHeader}`} faded>
				<div className={styles.pageHeaderContent}>
					<h1>
						{mobile ? (
							<span>
								It&apos;s time.
								<br />
								Let&apos;s Go Electric.
							</span>
						) : (
							<>It&apos;s time. Let&apos;s Go Electric.</>
						)}
					</h1>

					<p className={`${styles.subHeader} lease-text`}>Browse used EVs from franchise dealerships across Ireland.</p>
				</div>
			</PageHeader>

			{/* Mobile Needs */}
			{tablet && (
				<SearchMenu
					displayFilters={displayFilters}
					displaySort={displaySort}
					hideMenu={hideMenu}
					hideMenuOnSort={hideMenuOnSort}
					setHideMenuOnSort={setHideMenuOnSort}
					renderFilters={renderFilters}
					renderedSortBy={renderedSortBySelector}
				/>
			)}

			<div className={`${styles.vehicleSearchPage} page-holder`}>
				{tablet && <MobileControl tablet={tablet} setDisplayFilters={setDisplayFilters} setDisplaySort={setDisplaySort} />}

				<div className={styles.contentHolder}>
					{tablet !== undefined && !tablet && (
						<div className={styles.searchHolder}>
							{renderFilters()}
							{!isBoiWebsite && <GoogleAd adId={googleAdIds.horizontal_banner} className="side-ad-banner" displayId="side" sideBanner />}
						</div>
					)}

					<div className={styles.carsResultHolder}>
						<div className={styles.vehicleCountSortHolder}>
							<h1 className={styles.countText}>
								{`We've found `}
								<span>{count}</span>
								{` EV${count !== 1 ? "s" : ""} for you:`}
							</h1>

							{!tablet && (
								<div className={styles.sortByHolder}>
									<p>Sort by:</p>
									{renderedSortBySelector}
								</div>
							)}
						</div>

						{!isBoiWebsite && <GoogleAd adId={googleAdIds.horizontal_banner} className={styles.adBanner} make={slugMakes} model={slugModels} listingPage />}
						<div className={styles.carsContainer} ref={vehiclesRef}>
							{vehicles.length ? (
								<div className={`${styles.vehicleGrid} ${styles.adGrid}`}>{renderVehicles(vehicles, vehicleTypes.used, smallDesktop, isBoiWebsite)}</div>
							) : message.length ? (
								<p className={styles.message}>{message}</p>
							) : null}
							{loading ? <Loader className={styles.vehicleLoader} /> : null}
						</div>
					</div>
				</div>
			</div>

			<Footer />
		</>
	);
}
